import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { openAlert } from '../../../../components/alert/hooks';
import { mobileBrandQuery } from '../../../../services/react-query/optionMasterQuery';
import { getWarrantyEditRowFieldNames } from '../../../warranty/warranty-edit/form';
import { MAX_CLAIM_UPDATE_PRODUCT_MOBILES } from '../form';
import { useBarcodeQuery } from './useGetBarcodeQuery';
import { useGetWarrantyMobiles } from './useGetWarrantyMobiles';
import { useUpdateProduct } from './useUpdateProduct';

/**
 * ฟอร์มของ modal เปลี่ยนสินค้า — แยก useForm ออกจากหน้ารายละเอียดเคลม
 * เพราะหน้ารายละเอียดใช้ mobileBrandName / mobileModelName เป็นข้อความรวมหลายเครื่อง (ใช้แก้ไขต่อไม่ได้)
 * ตั้งค่าเริ่มต้นยี่ห้อ/รุ่นจากเครื่องที่ลงทะเบียนไว้ เพื่อให้ส่ง mobiles ครบตามที่ API ต้องการ (1-3 รายการ)
 */
export const useUpdateProductForm = ({ claimData }) => {
  const { control, getValues, setValue, reset, handleSubmit } = useForm();
  const { data: mobileBrandOptions } = mobileBrandQuery();
  const {
    warrantyMobiles,
    loading: isLoadingMobiles,
    ready: mobilesReady,
    hasError: mobileLoadFailed,
  } = useGetWarrantyMobiles(claimData?.warrantyId);
  const { mutate: updateProduct, loading: isLoadingProduct } = useUpdateProduct();
  const {
    mutate: onCallBarcode,
    loading: isLoadingBarcode,
    productOptions,
    onBarcodeChange,
    onSelectProduct,
    canSubmit,
  } = useBarcodeQuery({
    setValue,
    getValues,
  });

  const mobileList = useMemo(
    () => (warrantyMobiles || []).slice(0, MAX_CLAIM_UPDATE_PRODUCT_MOBILES),
    [warrantyMobiles],
  );

  useEffect(() => {
    if (!claimData) {
      return;
    }
    const values = {
      claimId: claimData.claimId,
      claimCode: claimData.claimCode,
      barcodeShortcut: claimData.barcodeShortcut,
      productId: claimData.productId,
      productName: claimData.productName,
      productTypeName: claimData.productTypeName,
      remark: '',
    };
    mobileList.forEach((item, index) => {
      const { brand, model, warrantyMobileId } = getWarrantyEditRowFieldNames(index);
      values[brand] = item.mobileBrandId;
      values[model] = item.mobileModelName;
      values[warrantyMobileId] = item.warrantyMobileId;
    });
    reset(values);
    // ตั้งค่าใหม่เฉพาะตอนเปลี่ยนใบเคลม/โหลดเครื่องที่ลงทะเบียนเสร็จ จะได้ไม่ล้างค่าที่ผู้ใช้กรอกค้างไว้
  }, [claimData?.claimId, mobileList, reset]);

  const submitValues = handleSubmit((values) => {
    if (!canSubmit(values)) {
      openAlert({ type: 'error', message: 'กรุณาค้นหาบาร์โค้ดและเลือกสินค้าก่อนบันทึก' });
      return;
    }
    updateProduct(values);
  });

  const onSubmit = (event) => {
    // An explicit zero identifies a claim without a registered warranty.
    const isUnregisteredClaim = claimData?.warrantyId === 0;
    if (!isUnregisteredClaim && !mobilesReady) {
      openAlert({
        type: 'error',
        message: mobileLoadFailed
          ? 'โหลดข้อมูลมือถือไม่สำเร็จ กรุณาปิดหน้าต่างแล้วเปิดใหม่'
          : 'กำลังโหลดข้อมูลมือถือ กรุณารอสักครู่',
      });
      return;
    }
    return submitValues(event);
  };

  const onEnterBarcode = (value) => {
    return onCallBarcode(value);
  };

  return {
    control,
    getValues,
    setValue,
    mobileBrandOptions,
    mobileRowCount: mobileList.length || 1,
    onEnterBarcode,
    onBarcodeChange,
    onSelectProduct,
    productOptions,
    onSubmit,
    loading: isLoadingMobiles || isLoadingBarcode || isLoadingProduct,
  };
};
